import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { DateNav } from '../Habit/components/DateNav'
import { HabitDashboard } from '../Habit/components/HabitDashboard'
import {
  HabitHeatmapGrid,
  HabitHeatmapLegend,
} from '../Habit/components/HabitHeatmapGrid'
import { getHabitDay, isSameCalendarDay } from '../Habit/utils/dateHelpers'
import {
  EMPTY_HABIT_STATS_PAGE_DATA,
  type HabitStatsPageData,
  loadHabitStatsPageData,
} from '../Habit/utils/habitData'
import { useDeviceMode } from '../Habit/utils/useDeviceMode'

function StatsContent(): ReactElement {
  const { isDesktop } = useDeviceMode()
  const user = useAuthStore((state) => state.user)
  const profile = useAuthStore((state) => state.profile)
  const dayStartTime = (profile?.day_start_time ?? '00:00:00').slice(0, 5)
  const realToday = useRef(new Date()).current
  const hasUserSelectedDate = useRef(false)
  const [selectedDate, setSelectedDate] = useState<Date>(() =>
    getHabitDay(new Date(), dayStartTime),
  )

  const handleDateChange = (date: Date): void => {
    hasUserSelectedDate.current = !isSameCalendarDay(
      date,
      realToday,
      dayStartTime,
    )
    setSelectedDate(date)
  }

  useEffect(() => {
    if (!hasUserSelectedDate.current) {
      setSelectedDate(getHabitDay(new Date(), dayStartTime))
    }
  }, [dayStartTime])

  const [pageData, setPageData] = useState<HabitStatsPageData>(
    EMPTY_HABIT_STATS_PAGE_DATA,
  )
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!user) return

    let cancelled = false
    setIsLoading(true)

    async function fetchStats(): Promise<void> {
      if (!user) return

      const loadedPageData = await loadHabitStatsPageData(
        user.id,
        selectedDate,
        dayStartTime,
      )

      if (!cancelled) {
        setPageData(loadedPageData)
        setIsLoading(false)
      }
    }

    fetchStats()

    return () => {
      cancelled = true
    }
  }, [user, selectedDate, dayStartTime])

  const hasHabits = pageData.heatmaps.length > 0
  const hasStats =
    pageData.dashboard.week.expected > 0 ||
    pageData.dashboard.month.expected > 0

  return (
    <div
      className={`mx-auto px-4 transition-all duration-200 ${
        isDesktop
          ? 'max-w-5xl py-6 pb-16 lg:max-w-6xl'
          : 'max-w-lg pt-4 pb-safe-24'
      }`}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <DateNav
          selectedDate={selectedDate}
          realToday={realToday}
          onChange={handleDateChange}
          dayStartTime={dayStartTime}
          isDesktop={isDesktop}
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-4 h-5 w-28 animate-pulse rounded bg-gray-100" />
            <div className="flex flex-col gap-4">
              <div className="h-10 animate-pulse rounded bg-gray-100" />
              <div className="h-10 animate-pulse rounded bg-gray-100" />
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-4 h-5 w-24 animate-pulse rounded bg-gray-100" />
            <HabitHeatmapGrid
              habit={{ frequency_type: 'daily' }}
              logs={[]}
              selectedDate={selectedDate}
              dayStartTime={dayStartTime}
              isLoading
            />
          </div>
        </div>
      ) : hasStats ? (
        <>
          <HabitDashboard stats={pageData.dashboard} />
          {hasHabits && (
            <section
              aria-label="Habit heatmaps"
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-base text-gray-900">
                    Heatmaps
                  </h2>
                  <p className="text-gray-500 text-xs">Last 12 weeks</p>
                </div>
                <span className="text-2xl" aria-hidden="true">
                  🗓️
                </span>
              </div>
              <div
                className={
                  isDesktop
                    ? 'grid grid-cols-1 gap-5 lg:grid-cols-2'
                    : 'flex flex-col gap-5'
                }
              >
                {pageData.heatmaps.map(({ habit, logs }) => (
                  <div key={habit.id}>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-gray-900 text-sm">
                          {habit.name}
                        </h3>
                        <p className="text-gray-500 text-xs">
                          {habit.category}
                        </p>
                      </div>
                    </div>
                    <HabitHeatmapGrid
                      habit={habit}
                      logs={logs}
                      selectedDate={selectedDate}
                      dayStartTime={dayStartTime}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <HabitHeatmapLegend />
              </div>
            </section>
          )}
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 border-dashed bg-white px-4 py-5 text-gray-400">
          <p className="text-sm">
            No active habits yet. Add habits on the main page to see progress
            here.
          </p>
        </div>
      )}
    </div>
  )
}

export default function StatsPage(): ReactElement {
  return (
    <UserGuard>
      <div className="min-h-screen bg-gray-50">
        <StatsContent />
      </div>
    </UserGuard>
  )
}
